import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Upload, Camera, Package, CheckCircle2, AlertTriangle, Truck, Store,
  ArrowLeft, Loader2, ScanLine, PackageCheck, PackageX,
  Plus, Minus, Trash2, Search, Pencil, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// --- Robust matching helpers (same approach as invoice scanning) ---
const normEan = (v) => (v == null ? "" : String(v)).replace(/\D/g, "");
const normArt = (v) => (v == null ? "" : String(v)).toUpperCase().replace(/[^A-Z0-9]/g, "");
const isGtin = (v) => { const d = normEan(v); return d.length >= 12 && d.length <= 14; };
const modelTokens = (name) => {
  if (!name) return [];
  return String(name).split(/[\s,;/]+/).map((t) => normArt(t))
    .filter((t) => t.length >= 4 && /[0-9]/.test(t) && /[A-Z]/.test(t));
};
// Indoor before outdoor ("ute..."), then alphanumeric
const pickBestLocation = (locs) => {
  const withStock = locs.filter((l) => (l.antal || 0) > 0 && l.lagerplats);
  if (withStock.length === 0) return null;
  withStock.sort((a, b) => {
    const au = a.lagerplats.toLowerCase().startsWith("ute");
    const bu = b.lagerplats.toLowerCase().startsWith("ute");
    if (au !== bu) return au ? 1 : -1;
    return a.lagerplats.localeCompare(b.lagerplats, "sv");
  });
  return withStock[0];
};

export default function ScanCustomerOrderPage() {
  const [step, setStep] = useState("upload"); // upload | review | result
  const [uploading, setUploading] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("store_sale");
  const [rows, setRows] = useState([]);
  const [picking, setPicking] = useState(false);
  const [result, setResult] = useState(null);

  const resolveProduct = async (item, orderNo) => {
    // Resolve the product + its locations + bookings via robust matching
    let inv = [];
    const eanDigits = normEan(item.ean) || (isGtin(item.artikelnummer) ? normEan(item.artikelnummer) : "");
    try {
      if (eanDigits) {
        inv = (await axios.get(`${API}/api/inventory/by-ean/${encodeURIComponent(eanDigits)}`)).data || [];
      }
    } catch { /* ignore */ }
    if (inv.length === 0) {
      const terms = [item.artikelnummer, ...modelTokens(item.produktnamn)].filter(Boolean);
      for (const t of terms) {
        try {
          const res = (await axios.get(`${API}/api/inventory`, { params: { search: t, limit: 5 } })).data;
          const matches = res?.items || res || [];
          if (matches.length > 0) {
            const ean = matches[0].ean;
            if (ean) inv = (await axios.get(`${API}/api/inventory/by-ean/${encodeURIComponent(ean)}`)).data || [];
            if (inv.length > 0) break;
          }
        } catch { /* ignore */ }
      }
    }

    const found = inv.length > 0;
    const ean = found ? inv[0].ean : (eanDigits || item.ean || "");
    const artikelnummer = found ? (inv[0].artikelnummer || item.artikelnummer) : (item.artikelnummer || "");
    const produktnamn = found ? (inv[0].produktnamn || item.produktnamn) : (item.produktnamn || "");
    const best = found ? pickBestLocation(inv) : null;

    // Bookings -> detect booked on another order
    let bookedOther = null;
    if (ean) {
      try {
        const bks = (await axios.get(`${API}/api/inventory/bookings/by-ean/${encodeURIComponent(ean)}`)).data || [];
        const other = bks.find((b) => (b.customer_order_number || "") && String(b.customer_order_number) !== String(orderNo));
        if (other) bookedOther = other.customer_order_number;
      } catch { /* ignore */ }
    }

    let status;
    if (!found) status = "not_found";
    else if (best) status = "in_stock";
    else status = "missing";

    return {
      key: Math.random().toString(36).slice(2),
      ean,
      artikelnummer,
      produktnamn,
      quantity: item.quantity || 1,
      location: best ? best.lagerplats : "",
      availableLocations: found ? inv.filter((l) => (l.antal || 0) > 0).map((l) => ({ lagerplats: l.lagerplats, antal: l.antal })) : [],
      status,
      bookedOther,
      include: status === "in_stock",
    };
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = (await axios.post(`${API}/api/purchasing/ocr/scan-customer-order`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
      if (!res.success) { toast.error(res.error || "Kunde inte läsa ordern"); return; }
      const ordNo = res.order_number || "";
      setOrderNumber(ordNo);
      if (res.delivery_method === "store_sale" || res.delivery_method === "route_pickup") {
        setDeliveryMethod(res.delivery_method);
      }
      const items = (res.items || []).filter((i) => (i.ean || i.artikelnummer || i.produktnamn));
      if (items.length === 0) { toast.error("Inga produktrader hittades i ordern"); return; }
      toast.info(`Matchar ${items.length} rader mot lagret...`);
      const resolved = [];
      for (const it of items) resolved.push(await resolveProduct(it, ordNo));
      setRows(resolved);
      setStep("review");
      toast.success(`${items.length} rader avlästa`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Uppladdning misslyckades");
    } finally {
      setUploading(false);
    }
  };

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const resolveByTerm = async (term, qty) => {
    const item = {
      ean: isGtin(term) ? normEan(term) : "",
      artikelnummer: term,
      produktnamn: term,
      quantity: qty || 1,
    };
    return await resolveProduct(item, orderNumber);
  };

  const addEmptyRow = () => setRows((prev) => [
    ...prev,
    {
      key: Math.random().toString(36).slice(2),
      ean: "", artikelnummer: "", produktnamn: "", quantity: 1,
      location: "", availableLocations: [], status: "not_found",
      bookedOther: null, include: false,
      editing: true, searchTerm: "", searching: false,
    },
  ]);

  const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

  const startEdit = (key) => updateRow(key, {
    editing: true,
    searchTerm: rows.find((r) => r.key === key)?.artikelnummer
      || rows.find((r) => r.key === key)?.ean || "",
  });

  const cancelEdit = (key) => {
    const r = rows.find((x) => x.key === key);
    // Drop a brand-new (never-resolved) empty row on cancel, otherwise just exit edit mode
    if (r && !r.ean && !r.artikelnummer && !r.produktnamn) { removeRow(key); return; }
    updateRow(key, { editing: false, searching: false, searchTerm: "" });
  };

  const runRowSearch = async (key, term, qty) => {
    const t = (term || "").trim();
    if (!t) { toast.error("Skriv artikelnummer, EAN eller namn"); return; }
    updateRow(key, { searching: true });
    try {
      const resolved = await resolveByTerm(t, qty);
      setRows((prev) => prev.map((r) => (r.key === key
        ? { ...resolved, key: r.key, quantity: qty || resolved.quantity, editing: false, searching: false, searchTerm: "" }
        : r)));
      if (resolved.status === "in_stock") toast.success(`Hittade ${resolved.artikelnummer || resolved.ean}`);
      else toast.warning("Produkten hittades inte i lagret");
    } catch {
      updateRow(key, { searching: false });
      toast.error("Sökningen misslyckades");
    }
  };

  const changeQty = (key, qty) => updateRow(key, { quantity: Math.max(1, qty) });

  const handlePick = async () => {
    const lines = rows.filter((r) => r.include && r.status === "in_stock" && r.location)
      .map((r) => ({ ean: r.ean, location: r.location, quantity: r.quantity, artikelnummer: r.artikelnummer, produktnamn: r.produktnamn }));
    if (lines.length === 0) { toast.error("Inga rader att plocka"); return; }
    setPicking(true);
    try {
      const res = (await axios.post(`${API}/api/inventory/pick-out-batch`, {
        order_reference: orderNumber,
        delivery_type: deliveryMethod,
        lines,
      })).data;
      setResult(res);
      setStep("result");
      toast.success(`${res.picked_count} rader utscannade`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Plock misslyckades");
    } finally {
      setPicking(false);
    }
  };

  const includedCount = rows.filter((r) => r.include && r.status === "in_stock").length;

  return (
    <div className="min-h-screen bg-background p-4 max-w-3xl mx-auto" data-testid="scan-customer-order-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/picking"><Button variant="ghost" size="icon" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button></Link>
        <ScanLine className="h-6 w-6 text-primary" />
        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Skanna kundorder</h1>
      </div>

      {step === "upload" && (
        <Card>
          <CardContent className="p-8 flex flex-col items-center gap-4 text-center">
            <Package className="h-14 w-14 text-muted-foreground" />
            <p className="text-muted-foreground">Ladda upp en PDF eller fota kundordern – systemet läser raderna och gör en färdig plocklista.</p>
            <div className="flex flex-col sm:flex-row gap-3 w-full justify-center mt-2">
              <label className="flex-1">
                <input type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} data-testid="upload-input" />
                <Button asChild className="w-full big-button" disabled={uploading}>
                  <span>{uploading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Upload className="h-5 w-5 mr-2" />}Ladda upp PDF/bild</span>
                </Button>
              </label>
              <label className="flex-1">
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])} disabled={uploading} data-testid="camera-input" />
                <Button asChild variant="outline" className="w-full big-button" disabled={uploading}>
                  <span><Camera className="h-5 w-5 mr-2" />Fota order</span>
                </Button>
              </label>
            </div>
            {uploading && <p className="text-sm text-muted-foreground">Läser av ordern...</p>}
          </CardContent>
        </Card>
      )}

      {step === "review" && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4 grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Ordernummer</label>
                <Input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Ordernummer" data-testid="order-number-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">Leveranssätt</label>
                <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                  <SelectTrigger data-testid="delivery-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="store_sale"><Store className="h-4 w-4 mr-2 inline" />Hämta i butik</SelectItem>
                    <SelectItem value="route_pickup"><Truck className="h-4 w-4 mr-2 inline" />Leverans på runda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            {rows.map((r) => {
              const color = r.editing ? "border-primary/40 bg-primary/5"
                : r.status === "in_stock" ? "border-green-500/40 bg-green-500/5"
                : "border-amber-500/40 bg-amber-500/5";
              const QtyStepper = (
                <div className="flex items-center border rounded-md overflow-hidden shrink-0" data-testid={`qty-stepper-${r.key}`}>
                  <button type="button" onClick={() => changeQty(r.key, r.quantity - 1)}
                    className="px-2 py-1 hover:bg-muted transition-colors" data-testid={`qty-dec-${r.key}`}>
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="px-2 text-sm font-medium min-w-[2rem] text-center">{r.quantity}</span>
                  <button type="button" onClick={() => changeQty(r.key, r.quantity + 1)}
                    className="px-2 py-1 hover:bg-muted transition-colors" data-testid={`qty-inc-${r.key}`}>
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
              );
              return (
                <Card key={r.key} className={color} data-testid={`order-row-${r.key}`}>
                  <CardContent className="p-3">
                    {r.editing ? (
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                          <Input
                            autoFocus
                            value={r.searchTerm}
                            onChange={(e) => updateRow(r.key, { searchTerm: e.target.value })}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runRowSearch(r.key, r.searchTerm, r.quantity); } }}
                            placeholder="Artikelnr, EAN eller namn"
                            className="h-9"
                            data-testid={`row-search-input-${r.key}`}
                          />
                          {QtyStepper}
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => cancelEdit(r.key)} data-testid={`row-cancel-${r.key}`}>
                            <X className="h-4 w-4 mr-1" />Avbryt
                          </Button>
                          <Button size="sm" onClick={() => runRowSearch(r.key, r.searchTerm, r.quantity)} disabled={r.searching} data-testid={`row-search-btn-${r.key}`}>
                            {r.searching ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}Sök
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <Checkbox checked={r.include} disabled={r.status !== "in_stock"}
                          onCheckedChange={(v) => updateRow(r.key, { include: !!v })}
                          className="mt-1" data-testid={`row-include-${r.key}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono font-medium">{r.artikelnummer || r.ean || "?"}</span>
                            {QtyStepper}
                            {r.status === "in_stock" && <Badge className="bg-green-600 text-white"><PackageCheck className="h-3 w-3 mr-1" />Finns</Badge>}
                            {r.status === "missing" && <Badge className="bg-amber-500 text-black"><PackageX className="h-3 w-3 mr-1" />Slut – restnoteras</Badge>}
                            {r.status === "not_found" && <Badge className="bg-amber-500 text-black"><AlertTriangle className="h-3 w-3 mr-1" />Ej i lagret</Badge>}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">{r.produktnamn || "—"}</p>
                          {r.bookedOther && (
                            <p className="text-xs text-orange-500 font-medium mt-1" data-testid={`row-booked-other-${r.key}`}>
                              ⚠ Bokad på annat ordernummer: {r.bookedOther}
                            </p>
                          )}
                          {r.status === "in_stock" && (
                            <div className="mt-2">
                              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Lagerplats</label>
                              {r.availableLocations.length > 1 ? (
                                <Select value={r.location} onValueChange={(v) => updateRow(r.key, { location: v })}>
                                  <SelectTrigger className="h-8 w-48" data-testid={`row-location-${r.key}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {r.availableLocations.map((l) => (
                                      <SelectItem key={l.lagerplats} value={l.lagerplats}>{l.lagerplats} ({l.antal} st)</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Badge variant="outline" className="ml-0 font-mono">{r.location}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => startEdit(r.key)} data-testid={`row-edit-${r.key}`} title="Ändra / sök om">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeRow(r.key)} data-testid={`row-remove-${r.key}`} title="Ta bort rad">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            <Button variant="outline" className="w-full border-dashed" onClick={addEmptyRow} data-testid="add-row-btn">
              <Plus className="h-4 w-4 mr-2" />Lägg till rad
            </Button>
          </div>

          <div className="sticky bottom-0 bg-background pt-2 pb-4">
            <Button className="w-full big-button" onClick={handlePick} disabled={picking || includedCount === 0} data-testid="pick-btn">
              {picking ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
              Plock ({includedCount} rader)
            </Button>
          </div>
        </div>
      )}

      {step === "result" && result && (
        <div className="space-y-4" data-testid="pick-result">
          <Card className="border-green-500/40 bg-green-500/5">
            <CardContent className="p-6 text-center">
              <CheckCircle2 className="h-14 w-14 mx-auto mb-3 text-green-500" />
              <h2 className="font-heading text-2xl font-bold mb-1">Plock klart!</h2>
              <p className="text-muted-foreground">
                {result.picked_count} utscannade · {result.skipped_count} överhoppade · Order {result.order_reference || "—"}
              </p>
            </CardContent>
          </Card>

          {result.picked.length > 0 && (
            <Card><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-green-600 mb-2 flex items-center gap-1"><PackageCheck className="h-4 w-4" />Utscannat</p>
              {result.picked.map((p, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                  <span className="font-mono">{p.artikelnummer || p.ean}</span>
                  <span>{p.picked_quantity} st @ {p.location}{p.reason ? ` · ${p.reason}` : ""}</span>
                </div>
              ))}
            </CardContent></Card>
          )}

          {result.skipped.length > 0 && (
            <Card className="border-amber-500/30"><CardContent className="p-4">
              <p className="text-xs uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1"><PackageX className="h-4 w-4" />Överhoppade / restnoterade</p>
              {result.skipped.map((s, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b last:border-0">
                  <span className="font-mono">{s.artikelnummer || s.ean}</span>
                  <span className="text-amber-600">{s.reason}</span>
                </div>
              ))}
            </CardContent></Card>
          )}

          <Button className="w-full" variant="outline" onClick={() => { setStep("upload"); setRows([]); setResult(null); setOrderNumber(""); }} data-testid="new-order-btn">
            Skanna ny order
          </Button>
        </div>
      )}
    </div>
  );
}
